import { FC, useEffect } from 'react';
import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from '@mui/material';
import { Download } from '@mui/icons-material';
import { DateTime } from 'luxon';
import { useDispatch, useSelector } from 'react-redux';
import {
	downloadPartnerReportRequest,
	getPartnerReportsRequest,
} from 'redux/reducers/referrals/reducer';
import {
	getDownloadingPartnerReportIds,
	getPartnerReports,
	getPartnerReportsIsLoad,
} from 'redux/reducers/referrals/selectors';
import {
	PARTNER_REPORTS_DIALOG_PAGE_SIZE,
	PARTNER_REPORTS_ORDER_BY,
} from 'redux/reducers/referrals/constants';
import { TPartnerReport } from 'redux/reducers/referrals/types';
import {
	formatReportPeriodName,
	getPartnerReportFileName,
} from 'components/ReferralsReports/utils';
import { TPartnerReportsDialogProps } from './types';

export const PartnerReportsDialog: FC<TPartnerReportsDialogProps> = ({
	open,
	onClose,
	partnerId,
	partnerName,
}) => {
	const dispatch = useDispatch();
	const reports = useSelector(getPartnerReports);
	const loading = useSelector(getPartnerReportsIsLoad);
	const downloadingIds = useSelector(getDownloadingPartnerReportIds);

	useEffect(() => {
		if (!open || partnerId === null) return;
		dispatch(
			getPartnerReportsRequest({
				pageNumber: 1,
				pageSize: PARTNER_REPORTS_DIALOG_PAGE_SIZE,
				filters: [
					{
						fieldName: 'partnerId',
						name: 'partnerId',
						value: String(partnerId),
						operator: '=',
						comparator: '=',
					},
				],
				orderBy: PARTNER_REPORTS_ORDER_BY,
			}),
		);
	}, [dispatch, open, partnerId]);

	const handleDownload = (report: TPartnerReport) => {
		if (downloadingIds.includes(report.id)) return;
		dispatch(
			downloadPartnerReportRequest({
				id: report.id,
				fallbackFileName: getPartnerReportFileName(report),
			}),
		);
	};

	// The list is shared with the reports page, so it is shown only once it holds this partner's data.
	const items = loading
		? []
		: (reports?.data ?? []).filter((report) => report.partnerId === partnerId);

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle>Fees reports – {partnerName}</DialogTitle>
			<DialogContent>
				{loading && <CircularProgress size={24} />}
				{!loading && !items.length && (
					<Typography color="text.secondary">There are no reports for this partner yet.</Typography>
				)}
				{!loading && !!items.length && (
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Period</TableCell>
								<TableCell>Created At</TableCell>
								<TableCell align="right" />
							</TableRow>
						</TableHead>
						<TableBody>
							{items.map((report) => (
								<TableRow key={report.id}>
									<TableCell>{formatReportPeriodName(report)}</TableCell>
									<TableCell>
										{DateTime.fromISO(report.createdAt).toFormat('dd.MM.yyyy, HH:mm')}
									</TableCell>
									<TableCell align="right">
										<Tooltip title="Download report">
											<span>
												<IconButton
													color="primary"
													size="small"
													disabled={downloadingIds.includes(report.id)}
													onClick={() => handleDownload(report)}
												>
													{downloadingIds.includes(report.id) ? (
														<CircularProgress size={18} />
													) : (
														<Download />
													)}
												</IconButton>
											</span>
										</Tooltip>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Close</Button>
			</DialogActions>
		</Dialog>
	);
};
